import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetEditInvoiceDetails } from "../../services/InvoiceService";
import EditInvoice from "./EditInvoice";
import OrderAttachmentsTable from "../lab-orders/OrderAttachmentsTable";
import InvoiceTransactionsTable from "./InvoiceTransactionsTable";
import EditInvoiceActions from "./EditInvoiceActions";
import ViewTransaction from "./ViewTransaction";
import CommonLoader from "../common/CommonLoader";
import { Typography, Divider, Box, Button } from "@mui/material";
import { NotificationManager } from "react-notifications";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { IconButton, Tooltip } from "@mui/material";

const EditInvoiceContainer = () => {
  const [loading, setLoading] = useState(false);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    getInvoice(invoiceNumber);
  }, [invoiceNumber]);

  const getInvoice = async (invoiceNumber) => {
    try {
      setLoading(true);
      let response = await GetEditInvoiceDetails(invoiceNumber);
      setInvoiceData(response.results);
    } catch (err) {
      NotificationManager.error("Error getting invoice. " + err, "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrderClick = (orderNumber) => {
    const orderUrl = `/vlp/lab-orders/${orderNumber}`;

    if (window.labOrderViewWindow && !window.labOrderViewWindow.closed) {
      // If the window is already open and not closed, update its location
      window.labOrderViewWindow.location.href = orderUrl;
    } else {
      // If the window is not open or closed, open a new one and store its reference
      window.labOrderViewWindow = window.open(
        orderUrl,
        "_blank",
        "resizable=yes,scrollbars=yes"
      );
    }
  };

  const goToNextInvoice = () => {
    const nextInvoiceNumber = parseInt(invoiceNumber) + 1;
    navigate(`/vlp/invoices/edit/${nextInvoiceNumber}`);
  };

  const goToPreviousInvoice = () => {
    const previousInvoiceNumber = parseInt(invoiceNumber) - 1;
    navigate(`/vlp/invoices/edit/${previousInvoiceNumber}`);
  };

  return (
    <div className="vlp-component-container" style={{ maxWidth: "100%", overflowX: "hidden" }}>
      {/* Header, Actions toggle and section styling mirror EditLabOrder so both edit screens look alike */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="title">
          Edit Invoice #{" "}
          <span style={{ color: "rgb(25,118,210)", fontWeight: "bold" }}>
            {invoiceNumber}
          </span>
        </span>

        <div className="order-navigation-buttons" style={{ display: "flex" }}>
          <Tooltip title="Move to Previous Invoice">
            <IconButton onClick={goToPreviousInvoice}>
              <ArrowBackRoundedIcon
                style={{ color: "rgb(25,118,210)", fontWeight: "bold" }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Move to Next Invoice">
            <IconButton onClick={goToNextInvoice}>
              <ArrowForwardRoundedIcon
                style={{ color: "rgb(25,118,210)", fontWeight: "bold" }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </Box>

      {invoiceData !== null && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1, mb: 1, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {invoiceData.orderNumber && (
              <span>
                Order #{" "}
                <a
                  style={{ color: "rgb(25,118,210)", fontWeight: "bold", cursor: "pointer" }}
                  onClick={() => handleViewOrderClick(invoiceData.orderNumber)}
                >
                  {invoiceData.orderNumber}
                </a>
              </span>
            )}
          </Box>
          <Button
            size="small"
            onClick={() => setActionsExpanded(!actionsExpanded)}
            endIcon={actionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: "none" }}
          >
            Actions
          </Button>
        </Box>
      )}

      {invoiceData !== null && (
        <EditInvoiceActions
          invoiceNumber={invoiceNumber}
          invoiceData={invoiceData}
          orderNumber={invoiceData.orderNumber}
          expanded={actionsExpanded}
        />
      )}
      <Box
        sx={{
          "& > .MuiPaper-root": { px: 0, mx: "0 !important" },
          "& .MuiAccordionSummary-root, & .MuiAccordionDetails-root": { px: 0 },
          "& .MuiTypography-h5, & .MuiTypography-h6, & .MuiTypography-h13": {
            fontSize: "1.1rem",
          },
          "& .MuiTypography-h13": { display: "block", mb: 1, color: "inherit !important" },
        }}
      >
        {invoiceData !== null && <EditInvoice invoiceData={invoiceData} />}
        {invoiceData !== null && <InvoiceTransactionsTable transactions={invoiceData.transactions} orderNumber={invoiceData.orderNumber} />}
        {invoiceData !== null && <OrderAttachmentsTable orderNumber={invoiceData.orderNumber} />}
      </Box>
      <CommonLoader loading={loading} />
    </div>
  );
};

export default EditInvoiceContainer;
